import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { PersonGridItemObject } from '@it2go/types';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { FolderPersonsStore } from '../folder-persons.store';
import { fb, router } from '../../../../../modules/shared/shared.global-service';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import { InfiniteScrollStore } from '../../../../../modules/shared/store/common/infinite-scroll.store';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { folderUri } from '../../../../../utils/router.utils';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { SaveButtonComponent } from '../../../../kit/button/save-button.component';
import { CancelButtonComponent } from '../../../../kit/button/cancel-button.component';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { FormatPersonPipe } from '../../../../../modules/shared/pipes/format-person.pipe';
import { folderPersonSearchFrag } from './folder-person-dialog.query';

/**
 * Přidání osoby do složky - vyhledání už existující osoby, nebo přechod na plný formulář nové osoby.
 * Data dialogu: { folderId: number, serviceId: number | null, excludePersonIds?: number[] }
 */
@Component({
    selector: 'app-folder-person-dialog',
    templateUrl: 'folder-person-dialog.component.html',
    providers: [FolderPersonsStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DatePipe,
        DateInputComponent,
        SelectInputComponent,
        SaveButtonComponent,
        CancelButtonComponent,
        ButtonComponent,
        FormatPersonPipe,
    ],
})
export class FolderPersonDialogComponent extends BaseDialogComponent {

    public readonly store = inject(FolderPersonsStore);

    protected readonly prefix = 'folder.persons.';

    protected readonly folderId = Number(this.dialogConfig.data?.folderId);
    protected readonly serviceId = Number(this.dialogConfig.data?.serviceId) || null;

    /** Osoby, které už ve složce jsou - backend by je odmítl kvůli překryvu pobytů. */
    private readonly excludePersonIds: number[] = this.dialogConfig.data?.excludePersonIds || [];

    protected form = new AppFormGroup({
        personId: fb.control<number | null>(null, Validators.required),
        dateFrom: fb.control<Date | null>(new Date(), Validators.required),
    });

    /**
     * Hledá se napříč organizací (ne jen mezi osobami služby) - backend pro přidání do složky
     * vyžaduje jen shodnou organizaci a osoba typicky na službě ještě není.
     * Zadaný text jde na server jako `search`, viz PersonGrid.getSearches() - jméno, alias, adresa, RČ.
     */
    protected personScroll = computed(() => {
        const organizationId = this.store.globalGlobalStore.organizationStore.selectedId();
        if (!organizationId) return null;

        return new InfiniteScrollStore<PersonGridItemObject>(
            this.store.api.getPersons,
            new FilterBuilder().where('organizationId', String(organizationId)).limit(20).filter(),
            true,
            folderPersonSearchFrag,
            (items) => items.filter((it) => !it.dateDeath && !this.excludePersonIds.includes(it.id)),
        );
    });

    /** Novou osobu zakládá plný formulář na routě `add-person`. */
    protected async createNewPerson(): Promise<void> {
        this.dialogRef.close(false);
        await router.navigateByUrl(folderUri(this.folderId, this.serviceId, 'add-person'));
    }

    public override async save(_event?: Event): Promise<void> {
        if (this.form.isInvalid()) return;

        await this.store.api.createFolderPerson({
            folderId: this.folderId,
            personId: this.form.value.personId!,
            dateFrom: this.form.value.dateFrom!.toISOString(),
        });

        contentGlobalSuccess.next(`${this.prefix}messages.added`);
        this.dialogRef.close(true);
    }

}
